import { IImportable } from 'library/ts/interfaces/importable/IImportable';
import { AbstractAdSetupFactory } from 'tag/ts/tag/factories/ads/AbstractAdSetupFactory';
import { IAdSetupInfo } from 'tag/ts/tag/interfaces/ads/IAdSetupInfo';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { ITagConfig } from 'tag/ts/tag/interfaces/config/ITagConfig';
import { AdSDK } from 'tag/ts/tag/model/type/ads/AdSDK';
import { AdSetupDefinition } from 'tag/ts/tag/model/type/ads/AdSetupDefinition';
import { AdSetupType } from 'tag/ts/tag/model/type/ads/AdSetupType';

/**
 * @copyright © Paramount Streaming
 * @author  Zeeshan Ahmad
 * @description Factory class used to create instances of IAdSetupInfo for playback on HTML5
 */
export class HTML5AdSetupFactory extends AbstractAdSetupFactory {

	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				name: 'HTML5AdSetupFactory',
				...argument
			}
		);
	}

	/**
	 * @description Returns a complex object representing ad setup for playback on HTML5
	 * @returns IAdSetupInfo
	 */
	createAdSetupInfo(): IAdSetupInfo {
		// sanity check
		if ( this.utils.Type.isEmpty( this.tag ) ) {
			this?.logger?.warn( 'createAdSetupInfo', 'tag is empty, cannot create adSetupInfo... exiting' );
			return undefined;
		}

		const tagConfig: ITagConfig = this.tag?.model?.config?.tag;
		const contentUtilities = this.tag?.model?.contentUtilities;
		const tagUtilities = this.tag?.tagUtilities;
		const params = this.tag?.params;
		const apiMetadata = this.tag?.model?.apiMetadata;

		const adSetupInfo = this.createDefaultAdSetupInfo();
		this?.logger?.log( 'createAdSetupInfo', 'created default adSetupInfo object requiring no ads:', adSetupInfo );

		// define booleans representing content characteristics
		const isLive: boolean = contentUtilities.isLive( params, apiMetadata );
		const isPID: boolean = contentUtilities.isPID( this.tag );
		const isLiveDAIStream: boolean = contentUtilities.isLiveDAIStream( apiMetadata );
		const isAssetURL: boolean = contentUtilities.isAssetURL( this.tag );
		const isInternational: boolean = tagUtilities.isInternationalContext( this.tag ) === true;
		const isLinearAdTrackable: boolean = contentUtilities.isLinearAdTrackable();
		const linearAdsRequestTemplate = this.tag?.model?.adUtilities?.getLinearAdTrackUrl?.();
		const hasLinearAdsRequestTemplate = (
			this.utils.Type.isString( linearAdsRequestTemplate )
			&&
			!this.utils.Type.isEmpty( linearAdsRequestTemplate )
		);
		const isClid3Eligible: boolean = isLinearAdTrackable && hasLinearAdsRequestTemplate;

		// IMPORTANT: CLID3-only (linear ad tracking) => definition: LINEAR_AD_TRACKING
		// - CLID3 + DAI must keep the DAI setup.
		if ( isClid3Eligible && isLive && isPID && !isLiveDAIStream ) {
			adSetupInfo.setup.definition = AdSetupDefinition.LINEAR_AD_TRACKING;
			this?.logger?.log( 'createAdSetupInfo', 'is linear ad trackable... updated setup definition:', adSetupInfo );
			return adSetupInfo;
		}

		// International does not require ads unless CLID3 + DAI needs the DAI API setup below.
		if ( isInternational && !( isClid3Eligible && isLive && isLiveDAIStream ) ) {
			this?.logger?.log( 'createAdSetupInfo', 'is INTL... no ad setup required:', adSetupInfo );
			return adSetupInfo;
		}

		// USE CASE: Stitcher SSAI => FAST (Free Ad-Supported Streaming) for Pluto TV. No SDK required, ads managed by Blackbird avia-js plugin.
		if ( this.hasStitcherParams() ) {
			adSetupInfo.setup.type = AdSetupType.SSAI;
			adSetupInfo.setup.definition = AdSetupDefinition.STITCHER;
			this?.logger?.log( 'createAdSetupInfo', 'stitcher params detected... setup ad playback via SSAI with Stitcher, no SDK required:', adSetupInfo );
			return adSetupInfo;
		}

		// Syncbak live streams do not require ads (but DAI live streams still need ad setup)
		if ( isAssetURL && isLive && !isLiveDAIStream ) {
			this?.logger?.log( 'createAdSetupInfo', 'asset is live stream via asset url... no ad setup required:', adSetupInfo );
			return adSetupInfo;
		}

		// use DAI API since html5 devices don't support IMA SDK
		if (
			isPID ||
			( isLive && isLiveDAIStream )
		) {
			adSetupInfo.setup.type = AdSetupType.SSAI; // Server Side Ad Insertion
			adSetupInfo.setup.definition = AdSetupDefinition.DAI_API; // DAI API
			adSetupInfo.sdk.type = AdSDK.DAI_API; // set ad SDK config module name
			adSetupInfo.sdk.module = tagConfig?.Imports?.params?.ad_sdk[ adSetupInfo.sdk.type ] as IImportable; // set ad SDK Import module from tag config
			this?.logger?.log( 'createAdSetupInfo', 'setup ad playback via SSAI with DAI API:', adSetupInfo );
			return adSetupInfo;
		}

		this?.logger?.log( 'createAdSetupInfo', 'no matching use case, returning default adSetupInfo:', adSetupInfo );
		return adSetupInfo;
	}
}